import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { DateTime } from "@dashboard/components/Date/DateTime";
import { DetailPageLayout } from "@dashboard/components/Layouts/Detail";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { Savebar } from "@dashboard/components/Savebar";
import {
  type AtticApiKeyFragment,
  AtticApiKeyStatusEnum,
  type AtticErrorFragment,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { sectionNames } from "@dashboard/intl";
import { type PermissionData } from "@dashboard/permissionGroups/components/PermissionGroupDetailsPage/PermissionGroupDetailsPage";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import {
  accessMessages,
  detailMessages,
  formMessages,
  listMessages,
  sectionMessages,
} from "../../messages";
import { AtticApiKeysDestinationIcon } from "../../topNav";
import { type AtticApiKeyFormData, type AtticApiKeySubmit } from "../../types";
import { atticApiKeyListPath } from "../../urls";
import {
  apiKeyToFormData,
  type AtticApiKeyUsageRow,
  expiryIsoToDay,
  isApiKeyFormDirty,
  isExpiryInputUnreadable,
  syncFormWithKey,
} from "../../utils";
import AtticApiKeyGeneralCard from "../AtticApiKeyGeneralCard/AtticApiKeyGeneralCard";
import { AtticApiKeyLastUsed } from "../AtticApiKeyListPage/AtticApiKeyListPage";
import AtticApiKeyPermissionsCard from "../AtticApiKeyPermissionsCard/AtticApiKeyPermissionsCard";
import AtticApiKeySecretsCard from "../AtticApiKeySecretsCard/AtticApiKeySecretsCard";
import AtticApiKeyStatusPill from "../AtticApiKeyStatusPill/AtticApiKeyStatusPill";
import AtticApiKeyUsageCard from "../AtticApiKeyUsageCard/AtticApiKeyUsageCard";

type Secret = AtticApiKeyFragment["secrets"][number];

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box display="flex" flexDirection="column" gap={1}>
    <Text size={2} color="default2">
      {label}
    </Text>
    <Box>{children}</Box>
  </Box>
);

export interface AtticApiKeyDetailsPageProps {
  apiKey: AtticApiKeyFragment;
  usageRows: AtticApiKeyUsageRow[];
  usageTotals: Omit<AtticApiKeyUsageRow, "day">;
  permissionChoices: PermissionData[];
  /** The key holds a permission the viewer does not, so the viewer cannot edit it. */
  exceeded: boolean;
  errors: AtticErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  addSecretState: ConfirmButtonTransitionState;
  now: number;
  minDay: string;
  onSubmit: AtticApiKeySubmit;
  onAddSecret: () => void;
  onDeleteSecret: (secret: Secret) => void;
  onRevoke: () => void;
}

export const AtticApiKeyDetailsPage = ({
  apiKey,
  usageRows,
  usageTotals,
  permissionChoices,
  exceeded,
  errors,
  disabled,
  saveButtonBarState,
  addSecretState,
  now,
  minDay,
  onSubmit,
  onAddSecret,
  onDeleteSecret,
  onRevoke,
}: AtticApiKeyDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const revoked = apiKey.status === AtticApiKeyStatusEnum.REVOKED;
  // Revoked is final. A key wider than the viewer is theirs to look at only:
  // the backend refuses every change to it (OUT_OF_SCOPE_APP), secrets and
  // revoke included, from anyone who lacks one of its permissions.
  const locked = exceeded && !revoked;
  const readOnly = revoked || exceeded;
  const [data, setData] = useState<AtticApiKeyFormData>(() => apiKeyToFormData(apiKey));
  // The page stays mounted across re-reads of the key (keyed on its id only),
  // so a re-read is merged into the form field by field instead.
  const savedForm = useRef(apiKeyToFormData(apiKey));

  useEffect(() => {
    const next = apiKeyToFormData(apiKey);
    const previous = savedForm.current;

    savedForm.current = next;
    setData(current => syncFormWithKey(current, previous, next));
  }, [apiKey]);

  const expiryRef = useRef<HTMLDivElement>(null);
  const change = (patch: Partial<AtticApiKeyFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const dirty = isApiKeyFormDirty(apiKey, data);

  return (
    <DetailPageLayout>
      <TopNav
        href={atticApiKeyListPath}
        hrefIcon={<AtticApiKeysDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.allKeys)}
        title={apiKey.label}
      >
        <AtticApiKeyStatusPill status={apiKey.status} />
      </TopNav>
      <DetailPageLayout.Content>
        <Box display="flex" flexDirection="column" gap={4} paddingBottom={6}>
          {revoked && (
            <Text
              as="p"
              size={3}
              color="critical1"
              paddingX={6}
              paddingTop={4}
              data-test-id="attic-api-key-revoked-notice"
            >
              {intl.formatMessage(formMessages.readOnlyRevoked)}
            </Text>
          )}
          {locked && (
            <Text
              as="p"
              size={3}
              color="default2"
              paddingX={6}
              paddingTop={4}
              data-test-id="attic-api-key-exceeded"
            >
              {intl.formatMessage(detailMessages.exceeded)}
            </Text>
          )}
          <AtticApiKeyGeneralCard
            data={data}
            errors={errors}
            disabled={disabled || readOnly}
            minDay={minDay}
            expired={apiKey.status === AtticApiKeyStatusEnum.EXPIRED}
            expiryRef={expiryRef}
            onChange={change}
          />
          <AtticApiKeySecretsCard
            secrets={apiKey.secrets}
            readOnly={revoked}
            locked={locked}
            addSecretState={addSecretState}
            onAddSecret={onAddSecret}
            onDeleteSecret={onDeleteSecret}
          />
          <AtticApiKeyUsageCard rows={usageRows} totals={usageTotals} />
        </Box>
      </DetailPageLayout.Content>
      <DetailPageLayout.RightSidebar>
        <DashboardCard data-test-id="attic-api-key-summary">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(detailMessages.summary)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="flex" flexDirection="column" gap={4}>
              <Fact label={intl.formatMessage(detailMessages.status)}>
                <AtticApiKeyStatusPill status={apiKey.status} />
              </Fact>
              <Fact label={intl.formatMessage(detailMessages.access)}>
                <Text size={3} data-test-id="attic-api-key-access">
                  {intl.formatMessage(
                    // A revoked or expired key cannot authenticate at all:
                    // "Read and write" would claim access it does not have.
                    apiKey.status !== AtticApiKeyStatusEnum.ACTIVE
                      ? accessMessages.noAccess
                      : apiKey.readOnly
                        ? accessMessages.readOnly
                        : accessMessages.readWrite,
                  )}
                </Text>
              </Fact>
              <Fact label={intl.formatMessage(detailMessages.created)}>
                <Text size={3}>
                  <DateTime date={apiKey.createdAt} plain />
                </Text>
              </Fact>
              <Fact label={intl.formatMessage(detailMessages.lastUsed)}>
                <AtticApiKeyLastUsed apiKey={apiKey} now={now} />
                <Text size={2} color="default2">
                  {intl.formatMessage(detailMessages.lastUsedHelp)}
                </Text>
              </Fact>
              <Fact label={intl.formatMessage(listMessages.expires)}>
                <Text size={3} data-test-id="attic-api-key-expires">
                  {apiKey.expiresAt ? (
                    <DayLabel date={expiryIsoToDay(apiKey.expiresAt)} plain />
                  ) : (
                    intl.formatMessage(listMessages.never)
                  )}
                </Text>
              </Fact>
              {apiKey.revokedAt && (
                <Fact label={intl.formatMessage(detailMessages.revokedAt)}>
                  <Text size={3} data-test-id="attic-api-key-revoked-at">
                    <DateTime date={apiKey.revokedAt} plain />
                  </Text>
                </Fact>
              )}
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
        <AtticApiKeyPermissionsCard
          choices={permissionChoices}
          selected={data.permissions}
          errors={errors}
          disabled={disabled || readOnly}
          onChange={permissions => change({ permissions })}
        />
      </DetailPageLayout.RightSidebar>
      <Savebar>
        {!revoked && (
          <Savebar.DeleteButton
            onClick={onRevoke}
            disabled={locked}
            data-test-id="attic-api-key-revoke"
          >
            {intl.formatMessage(detailMessages.revoke)}
          </Savebar.DeleteButton>
        )}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(atticApiKeyListPath)} />
        {!readOnly && (
          <Savebar.ConfirmButton
            transitionState={saveButtonBarState}
            // The shared ConfirmButton ignores `disabled` for 3s after a save
            // (its success tick), so the click is guarded too: no changes, no
            // request and no second "Saved".
            onClick={() => {
              if (!disabled && dirty && data.label.trim() !== "") {
                onSubmit(data, isExpiryInputUnreadable(expiryRef.current));
              }
            }}
            disabled={disabled || !dirty || data.label.trim() === ""}
            data-test-id="attic-api-key-save"
          />
        )}
      </Savebar>
    </DetailPageLayout>
  );
};

/** The detail page's frame without a key: the section's name and a way back. */
const KeyFrame = ({ children }: { children: React.ReactNode }) => {
  const intl = useIntl();

  return (
    <ListPageLayout>
      <TopNav
        href={atticApiKeyListPath}
        hrefIcon={<AtticApiKeysDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.allKeys)}
        title={intl.formatMessage(sectionNames.atticApiKeys)}
      />
      <DashboardCard>
        <DashboardCard.Content>{children}</DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

/** While the key loads. */
export const AtticApiKeyLoading = () => (
  <KeyFrame>
    <Box display="flex" flexDirection="column" gap={3} data-test-id="attic-api-key-detail-loading">
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </Box>
  </KeyFrame>
);

/** No key to show: there is none by this id, or the read failed. */
export const AtticApiKeyMissing = ({ loadFailed = false }: { loadFailed?: boolean }) => {
  const intl = useIntl();

  return (
    <KeyFrame>
      {loadFailed ? (
        <Text size={3} color="critical1" data-test-id="attic-api-key-detail-load-failed">
          {intl.formatMessage(sectionMessages.loadFailed)}
        </Text>
      ) : (
        <Text size={3} color="default2" data-test-id="attic-api-key-detail-missing">
          {intl.formatMessage(sectionMessages.notFound)}
        </Text>
      )}
    </KeyFrame>
  );
};

export default AtticApiKeyDetailsPage;
